import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  PrimaryButton,
  DefaultButton,
  makeStyles,
  Theme,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { Warning } from '@bb-ui/icons/dist/small/Warning';

interface CancelExecutionConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cohortName: string;
  loading?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: '500px',
    maxWidth: '600px',
  },
  warningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
  warningIcon: {
    fontSize: '24px',
  },
  contentSection: {
    marginBottom: theme.spacing(2),
  },
  consequencesList: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1.5),
    '& li': {
      marginBottom: theme.spacing(1),
      lineHeight: 1.6,
    },
  },
  errorBox: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.error.light,
    borderRadius: theme.shape.borderRadius,
    border: `2px solid ${theme.palette.error.main}`,
    marginTop: theme.spacing(2),
  },
  errorText: {
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1.5),
  },
  confirmButton: {
    minWidth: '180px',
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.main,
    },
  },
}));

const CancelExecutionConfirmDialog: React.FC<CancelExecutionConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  cohortName,
  loading = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handleConfirm = () => {
    if (!loading) {
      onConfirm();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle onClose={!loading ? onClose : undefined} closeLabel={t('global.close')}>
        {t('cohorts.cancelExecutionDialog.title')}
      </DialogTitle>

      <DialogContent>
        <div className={classes.warningHeader}>
          <Warning className={classes.warningIcon} />
          <Typography variant="h6">{t('cohorts.cancelExecutionDialog.warningHeader')}</Typography>
        </div>

        <div className={classes.contentSection}>
          <Typography variant="body1" gutterBottom>
            {t('cohorts.cancelExecutionDialog.description', { cohortName })}
          </Typography>

          <Typography variant="body1" gutterBottom style={{ marginTop: '16px' }}>
            {t('cohorts.cancelExecutionDialog.consequences')}
          </Typography>

          <ul className={classes.consequencesList}>
            <li>
              <Typography variant="body2">
                {t('cohorts.cancelExecutionDialog.consequence1')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('cohorts.cancelExecutionDialog.consequence2')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t('cohorts.cancelExecutionDialog.consequence3')}
              </Typography>
            </li>
          </ul>
        </div>

        <div className={classes.errorBox}>
          <Typography className={classes.errorText}>
            {t('cohorts.cancelExecutionDialog.warning')}
          </Typography>
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <DefaultButton onClick={onClose} disabled={loading}>
          {t('global.cancel')}
        </DefaultButton>
        <PrimaryButton onClick={handleConfirm} className={classes.confirmButton} disabled={loading}>
          {loading ? (
            <>{t('cohorts.cancelExecutionDialog.canceling')}</>
          ) : (
            t('cohorts.cancelExecutionDialog.confirm')
          )}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default CancelExecutionConfirmDialog;
